import { useComputed, useSignal } from '@preact/signals';
import { Chip, type IconPath } from '@nespresso-design-system/components';
import cn from 'classnames';
import type { ComponentChildren } from 'preact';
import styles from './FilterDrawer.module.scss';
import useTranslations from '@/widgets/hooks/useTranslations';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand.tsx';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore';
import type {
  FilterOpenModal,
  PersistedState
} from '@/widgets/ProductList/Store';
import { trackFilterDropdownOpened } from '@/widgets/ProductList/Tracking.ts';
import useMediaQuery from '@/widgets/hooks/useMediaQuery';
import type { Filter } from '@/domain/FilteringAndSorting.tsx';

export type FilterDrawerProps = {
  filter: Filter;
  headingModal: string;
  icon?: IconPath;
  children: ComponentChildren | ((toggle: () => void) => ComponentChildren);
  class?: string;
  isSelected?: boolean;
};

export default function FilterDrawer({
  filter,
  headingModal,
  icon,
  class: className,
  children,
  isSelected
}: FilterDrawerProps) {
  const { t } = useTranslations();
  const { isOpen, toggle, onTouchStart, onTouchEnd } = useFilterToggle(filter);
  const content = typeof children === 'function' ? children(toggle) : children;

  return (
    <div class={cn(styles.container, className)} id={filter.id}>
      <Chip
        aria-label={filter.label}
        label={filter.label}
        onClick={toggle}
        isSelected={isSelected}
        isActive={isOpen.value}
        intent="expandable"
        icon={icon}
      />

      <ModalOnDemand
        closeBtnA11yLabel={t('components.modal.closeBtnA11yLabel')}
        heading={headingModal}
        onClose={() => isOpen && toggle()}
        isOpen={isOpen.value}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {content}
      </ModalOnDemand>
    </div>
  );
}

function useFilterToggle(filter: Filter) {
  const { isMobile } = useMediaQuery();
  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();
  const swipeStartY = useSignal<number | null>(null);
  const modalId = `filter_${filter.id}` as FilterOpenModal;
  const isOpen = useComputed(() => openModal.value === modalId);

  function toggle() {
    const isDrawerOpened = isOpen.peek();
    if (!isDrawerOpened) {
      trackFilterDropdownOpened(filter);
    }
    setPersistedState({ openModal: isDrawerOpened ? undefined : modalId });
  }

  // this code will be removed as this code will be implemented in DS
  // as per https://nestle.atlassian.net/browse/DSNES-1565
  function handleTouchStart(event: TouchEvent) {
    if (event.touches.length === 1) {
      swipeStartY.value = event.touches[0].clientY;
    }
  }

  function handleTouchEnd(event: TouchEvent) {
    if (swipeStartY.value === null || isMobile) {
      return;
    }

    const swipeEndY = event.changedTouches[0].clientY;
    const swipeDistance = swipeEndY - swipeStartY.value;
    const minSwipeDistance = 50;

    if (swipeDistance > minSwipeDistance) {
      setPersistedState({ openModal: undefined });
    }

    swipeStartY.value = null;
  }

  return {
    isOpen,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    toggle
  };
}
